import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  ClipboardList, ArrowLeft, RefreshCw, Filter,
  CheckCircle, XCircle, Send, RotateCcw, Search, FileText
} from 'lucide-react';

export default function FiscalLogs() {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filtros, setFiltros] = useState({
    operacao: ''
  });
  
  const navigate = useNavigate();
  const limit = 30;

  useEffect(() => {
    loadLogs();
  }, [page, filtros.operacao]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filtros.operacao) params.operacao = filtros.operacao;

      const response = await api.get('/fiscal/logs', { params });
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  };

  const getOperacaoIcon = (operacao) => {
    const icons = {
      emissao: Send,
      cancelamento: XCircle,
      consulta: Search,
      reprocessamento: RotateCcw
    };
    return icons[operacao] || FileText;
  };

  const getOperacaoBadge = (operacao) => {
    const styles = {
      emissao: 'bg-blue-100 text-blue-700',
      cancelamento: 'bg-red-100 text-red-700',
      consulta: 'bg-gray-100 text-gray-700',
      reprocessamento: 'bg-yellow-100 text-yellow-700'
    };
    const Icon = getOperacaoIcon(operacao);
    return (
      <Badge className={`${styles[operacao] || styles.consulta} gap-1`}>
        <Icon className="w-3 h-3" />
        {operacao}
      </Badge>
    );
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6 space-y-6" data-testid="fiscal-logs">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/fiscal')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="w-6 h-6" />
            Logs e Auditoria
          </h1>
          <p className="text-muted-foreground">Histórico de operações fiscais</p>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Tipo de Operação</Label>
              <Select 
                value={filtros.operacao || "_all"} 
                onValueChange={(v) => setFiltros(prev => ({ ...prev, operacao: v === "_all" ? "" : v }))}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todas</SelectItem>
                  <SelectItem value="emissao">Emissão</SelectItem>
                  <SelectItem value="cancelamento">Cancelamento</SelectItem>
                  <SelectItem value="consulta">Consulta</SelectItem>
                  <SelectItem value="reprocessamento">Reprocessamento</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button variant="outline" onClick={loadLogs}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de logs */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            Registros ({total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardList className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nenhum log encontrado.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Operação</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Nota ID</TableHead>
                    <TableHead>Detalhes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell className="whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        {getOperacaoBadge(log.operacao)}
                      </TableCell>
                      <TableCell>
                        {log.sucesso ? (
                          <Badge className="bg-green-100 text-green-700 gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Sucesso
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 gap-1">
                            <XCircle className="w-3 h-3" />
                            Erro
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{log.usuario_nome || 'Sistema'}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {log.nota_id ? log.nota_id.substring(0, 8) + '...' : '-'}
                      </TableCell>
                      <TableCell className="max-w-[250px]">
                        {log.erro ? (
                          <span className="text-red-600 text-sm">{log.erro}</span>
                        ) : log.detalhes ? (
                          <span className="text-sm text-muted-foreground">
                            {log.detalhes.aluno && `Aluno: ${log.detalhes.aluno}`}
                            {log.detalhes.valor && ` | R$ ${log.detalhes.valor}`}
                            {log.detalhes.motivo && log.detalhes.motivo}
                          </span>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Anterior
              </Button>
              <span className="flex items-center px-4 text-sm text-muted-foreground">
                Página {page} de {totalPages}
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Próxima
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
